import React from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { PageHeader, Button, BackToListLink } from '@/components/shared/ui';
import { ROUTES } from '@/lib/constants/routes';
import { useApiLanguage } from '@/hooks/useApiLanguage';
import { resourceCategoryApiToUi } from '@/lib/resourceCategoryMap';
import { deleteResourcePost, getResourcePostDetail } from '@/services/resourcePostService';
import type { ResourceCategory } from '@/data/resources';

const CATEGORY_LABEL_KEYS: Record<Exclude<ResourceCategory, 'all'>, string> = {
  productInfo: 'resourcePage.categoryProductInfo',
  guide: 'resourcePage.categoryGuide',
  education: 'resourcePage.categoryEducation',
  simulationRoom: 'resourcePage.categorySimulationRoom',
};

/**
 * 리소스 상세 — GET /resource-post/:id/detail + language
 */
export default function ResourceDetail(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('menu');
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = location.pathname.startsWith('/admin');
  const lang = useApiLanguage();
  const queryClient = useQueryClient();

  const resourcePostId = id != null && /^\d+$/.test(id) ? Number(id) : NaN;

  const { data: detail, isLoading, isError, error } = useQuery({
    queryKey: ['resource-post', 'detail', resourcePostId, lang, isAdmin],
    queryFn: () => getResourcePostDetail(resourcePostId, lang, { skipAuth: !isAdmin }),
    enabled: Number.isFinite(resourcePostId) && resourcePostId > 0,
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteResourcePost(resourcePostId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['resource-post'] });
    },
  });

  const backTo = isAdmin ? ROUTES.ADMIN.RESOURCES : ROUTES.COMMON.RESOURCES;
  const wrapperClass = 'max-w-[1280px] mx-auto px-4 py-8';

  const handleDeleteResource = () => {
    if (window.confirm('이 자료를 삭제하시겠습니까?')) {
      deleteMut.mutate(undefined, {
        onSuccess: () => navigate(backTo),
      });
    }
  };

  if (!id || !Number.isFinite(resourcePostId) || resourcePostId <= 0) {
    return (
      <div className={wrapperClass}>
        <BackToListLink to={backTo} className="mb-6">
          {t('resourcePage.backToList')}
        </BackToListLink>
        <p className="text-gray-500">{t('resourcePage.notFound')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={wrapperClass}>
        <BackToListLink to={backTo} className="mb-6">
          {t('resourcePage.backToList')}
        </BackToListLink>
        <p className="text-gray-500">불러오는 중…</p>
      </div>
    );
  }

  if (isError || !detail) {
    return (
      <div className={wrapperClass}>
        <BackToListLink to={backTo} className="mb-6">
          {t('resourcePage.backToList')}
        </BackToListLink>
        <p className="text-gray-500">
          {error instanceof Error ? error.message : t('resourcePage.notFound')}
        </p>
      </div>
    );
  }

  const uiCategory = resourceCategoryApiToUi(detail.category);
  const categoryLabelKey = CATEGORY_LABEL_KEYS[uiCategory];
  const categoryDescription =
    (categoryLabelKey ? t(categoryLabelKey) : '').trim() ||
    String(detail.category ?? '').trim() ||
    undefined;

  return (
    <div className={wrapperClass}>
      <BackToListLink to={backTo} className="mb-4">
        {t('resourcePage.backToList')}
      </BackToListLink>

      <PageHeader
        title={detail.title}
        description={categoryDescription}
        rightContent={
          isAdmin ? (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="md"
                onClick={handleDeleteResource}
                disabled={deleteMut.isPending}
                className="text-red-600 hover:text-red-700 hover:border-red-300"
              >
                <Trash2 size={16} className="mr-2" />
                삭제
              </Button>
              <Link to={ROUTES.ADMIN.RESOURCES_EDIT(String(detail.id))}>
                <Button variant="outline" size="md">
                  <Pencil size={16} className="mr-2" />
                  {t('resourcePage.editButton')}
                </Button>
              </Link>
            </div>
          ) : null
        }
      />

      <div className="mt-6">
        {detail.content?.trim() ? (
          <div
            className="resource-detail-body rounded-lg border border-gray-200 bg-white p-6 text-gray-700"
            dangerouslySetInnerHTML={{ __html: detail.content.trim() }}
          />
        ) : detail.description?.trim() ? (
          <p className="text-gray-600 whitespace-pre-wrap">{detail.description}</p>
        ) : (
          <p className="text-gray-500 text-sm">표시할 내용이 없습니다.</p>
        )}
      </div>
    </div>
  );
}
